import React, { memo, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { variationDetails, saveVariation, removeVariation, copyVariation } from "../../services/exercise_collections";
import Switch from "../../components/shared/switch";

import ContentTranslation from "../../components/ContentTranslation";
import { VariationContentItem } from "../../components/variation_content_preview";
import AgeSlider from "../../components/AgeSlider";
import Select from "react-select";
import { selectStyles } from "../../components/shared/styles";
import { filterEmptyTranslations } from "../../services/filters";
import LibraryDialog from "../../components/shared/libraryDialog";
import ConfirmRemoveDialog from '../../components/ConfirmRemoveDialog';
import CommentsButton from '../../components/CommentsButton';
import ReturnButton from '../../components/ReturnButton';

const ExerciseCollectionCollectionForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, clone_id } = useParams();
  const fieldShapes = ["flexible", "square", "rectangle"];

  const { t } = useTranslation();

  const [language, setLanguage] = useState('en')
  const [exerciseCollection, setExerciseCollection] = useState({
    exercise_collections_exercises_attributes: [],
    exercise_collection_translations_attributes: [{
      title: '',
      content: '',
      locale: language,
      published: true
    }]
  })
  const [hoveredItem, setHoveredItem] = useState(null);
  const [pickItem, setPickItem] = useState(null)

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const loadData = async () => {
    if (id) {
      const { exercise_collection } = await variationDetails(id);
      setExerciseCollection(exercise_collection);
    } else if (clone_id) {
      try {
        const { exercise_collection } = await copyVariation(clone_id);
        setExerciseCollection(exercise_collection);
      } catch (e) {
        navigate(-1)
      }
    }
  };

  useEffect(() => {
    loadData()
  }, [location.pathname])

  const save = async () => {
    const data = { ...exerciseCollection }
    data.exercise_collection_translations_attributes = filterEmptyTranslations(exerciseCollection.exercise_collection_translations_attributes)
    const response = await saveVariation(data);
    navigate("/club_admin/exercise_collections/");
  }

  const handleChangeItem = (exercise_collectible_type, item) => {
    const collectionItem = {...item, exercise_collectible_type, exercise_collectible_id: item?.id, position: pickItem, id: null, _destroy: false};

    let exercise_collections_exercises_attributes = [...exerciseCollection.exercise_collections_exercises_attributes];

    let index = exerciseCollection.exercise_collections_exercises_attributes.findIndex(el => el?.position === pickItem);
    if (index !== -1) {
      exercise_collections_exercises_attributes.splice(index, 1, {...collectionItem, id: exercise_collections_exercises_attributes[index].id});
    } else {
      exercise_collections_exercises_attributes.push(collectionItem);
    }

    setExerciseCollection({ ...exerciseCollection, exercise_collections_exercises_attributes });
    setPickItem(null);
  };

  const remove = async () => {
    if (!exerciseCollection?.id || !!exerciseCollection.locked) {
      return;
    }
    setShowConfirmDialog(true);
  }

  const confirmRemove = async () => {
    await removeVariation(id)
    navigate("/club_admin/exercise_collections/")
  };


  const deleteItem = position => {
    let updatedExercises = [...exerciseCollection.exercise_collections_exercises_attributes];
    const item = updatedExercises.find(item => item.position === position);

    if (item.id) {
      let index = updatedExercises.findIndex(el => el.position === position);
      updatedExercises.splice(index, 1, { id: item.id, position, _destroy: true });
    } else {
      updatedExercises = updatedExercises.filter(item => item.position !== position);
    }

    setExerciseCollection({ ...exerciseCollection, exercise_collections_exercises_attributes: updatedExercises });
  };

  const clone = () => {
    navigate(`/club_admin/exercise_collections/${id}/copy`)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="form-container exerciseCollection-form">
      <ReturnButton/>
      <div className="form-container-header">        
        { id && t('exercise_collections.edit')}
        { clone_id && t('exercise_collections.copy')}
        { !id && !clone_id && t('exercise_collections.new')}
      </div>
      <div className="form-container-header">
        <div className="form-container-header-title flex1">
          {exerciseCollection?.title}
        </div>
        <div className="form-container-header-status">{t('common.published')}<Switch value={exerciseCollection?.published}
                                                                       onChange={(value) => setExerciseCollection({
                                                                         ...exerciseCollection,
                                                                         published: value
                                                                       })}/></div>
      </div>
      <div className="form-container-area">
        <div className="form-container-area-group flex-between">
          <div className="flex-column">
            <div className="heading">{hoveredItem?.title || t('exercise_collections.variations_order')}</div>
            <div className="variation-order-preview">
              <div className={`variation-order-preview-full_size ${hoveredItem ? 'make-visible' : ''}`}>
                <VariationContentItem {...{ item: hoveredItem, height: 320, width: 320 }} />
              </div>
              <div className="variation-order-preview-list">
                {[1, 2, 3, 4].map(position => {
                    const item = exerciseCollection?.exercise_collections_exercises_attributes?.find(el => el.position === position && el._destroy !== true)
                    return <div key={position} className="variation-order-preview-list-item">
                    <div>{!item ? `${position}. ${t('exercise_collections.choose_exercises')}` : <div># {item?.title}</div>}
                    </div>
                    </div>
                  }
                )}
              </div>

            </div>
          </div>

          <div className="flex1 flex-column">
            <div className="heading">{t('exercise_collections.field_shape')}</div>
            <Select
              styles={selectStyles}
              closeMenuOnSelect={true}
              options={fieldShapes.map(item => ({ value: item, label: t(`exercise_collections.field_shapes.${item}`)}))}
              default={'square'}
              isSearchable={false}
              onChange={(selected) => setExerciseCollection({...exerciseCollection, field_shape: selected.value })}
              value={{ value: exerciseCollection?.field_shape ,  label: t(`exercise_collections.field_shapes.${exerciseCollection?.field_shape}`)}}
            />
          </div>

          <div>
            <div className="heading">{t('exercise_collections.choose_exercises')}</div>
            <div className="exercise-collection-items">

              {[1, 2, 3, 4].map(position => {
                const item = exerciseCollection?.exercise_collections_exercises_attributes?.find(el => el.position === position && el._destroy !== true)

                return <div key={position} className='variation-manage-item' onMouseOver={() => item && setHoveredItem(item)} onMouseLeave={() => setHoveredItem(null)}>
                  <div className='variation-manage-item-controls'>
                    <button className="button button-primary" onClick={() => {setPickItem(position)}}>{!item ? t('exercise_collections.add') : t('common.change')}</button>
                    { item && <button className="button button-delete" onClick={() => deleteItem(position)}>{t('common.remove')}</button> }
                  </div>
                  <VariationContentItem {...{ item, height: 158, width: 158, position }} />
                </div>
              })}

            </div>

          </div>
        </div>


      </div>

        <div className="form-container-content">
          <div className="exercise-form-year_span">
          <div className="heading">{t('common.year_span')}</div>
          <AgeSlider
            minimum_age={exerciseCollection?.minimum_age}
            maximum_age={exerciseCollection?.maximum_age}
            onChange={({ minimum_age, maximum_age }) => setExerciseCollection({ ...exerciseCollection, minimum_age, maximum_age })}>
          </AgeSlider>
        </div>
        <div className="form-container-content-right-narrow"></div>
      </div>


      <div className="form-container-content">
        <ContentTranslation data={exerciseCollection?.exercise_collection_translations_attributes}
                            onChange={values => setExerciseCollection({
                              ...exerciseCollection,
                              exercise_collection_translations_attributes: values
                            })}/>
         {exerciseCollection.id && <CommentsButton entityType= {'exercise_collections'}/> }
      </div>

      <div className="form-container-actions">
        <button className='button button-delete' onClick={remove}>{t('exercise_collections.delete')}</button>
        { id && <button className='button button-clone' onClick={clone}>
          {t('exercise_collections.copy_button')} <i className="icon icon-docs pointer"></i>
        </button> }
        <div className="flex-end">
          <button className="button" onClick={() => navigate("/club_admin/exercise_collections/")}>{t('buttons.common.cancel')}</button>
          <button className="button button-primary" onClick={save}>{t('buttons.common.save-changes')}</button>
        </div>

      </div>

     { pickItem !== null && <LibraryDialog close={() => setPickItem(null)} onChange={handleChangeItem} libraries={['Exercise','Aktion']}/> }

     {showConfirmDialog && (
        <ConfirmRemoveDialog
          title={t(`exercise_collections.remove_exercise_collections`, { title: exerciseCollection?.title  })}
          confirm={confirmRemove}
          close={() => setShowConfirmDialog(false)}
        />
      )}
    </div>

  )
};

export default memo(ExerciseCollectionCollectionForm);
